import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {useAiRecordAudio} from '@Components/ai-prompt-box/hooks/useAiRecordAudio';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AudioWaveformVisualizer} from '@Components/ai-prompt-box/components/audio-waveform-visualizer/audio-waveform-visualizer';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import recordAudioStyles from '@Components/ai-prompt-box/components/ai-record-audio-button/ai-record-audio-button.module.scss';
import actionButtonStyles from '../../ai-prompt-box-action-button.module.scss';

const SIZE_CLASS_MAP = {
  [AiPromptBoxSize.SMALL]: actionButtonStyles.actionButtonSmall,
  [AiPromptBoxSize.MEDIUM]: actionButtonStyles.actionButtonMedium,
  [AiPromptBoxSize.SINGLE_LINE]: actionButtonStyles.actionButtonSingleLine,
  [AiPromptBoxSize.SMALL_EXTENDED]: actionButtonStyles.actionButtonSmall,
};

const SIZE_HEIGHT_MAP = {
  [AiPromptBoxSize.SMALL]: 32,
  [AiPromptBoxSize.MEDIUM]: 40,
  [AiPromptBoxSize.SINGLE_LINE]: 32,
  [AiPromptBoxSize.SMALL_EXTENDED]: 32,
};

const SIZE_ICON_SIZE_MAP = {
  [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
  [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
  [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
  [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
};

interface AiRecordingControlsProps {
  id: string;
}

export function AiRecordingControls({id}: AiRecordingControlsProps): ReactElement | null {
  const recordAudio = useAiRecordAudio(id);
  const {state} = useAiPromptBox(id);
  const [isStopping, setIsStopping] = useState(false);

  useEffect(() => {
    if (!recordAudio.isRecording && !recordAudio.isProcessing) {
      setIsStopping(false);
    }
  }, [recordAudio.isRecording, recordAudio.isProcessing]);

  const showMic = state.transcription?.showMic ?? false;

  if (!showMic || (!recordAudio.isRecording && !recordAudio.isProcessing && !isStopping)) {
    return null;
  }

  const barWidth = state.transcription?.visualizerBarWidth;
  const gap = state.transcription?.visualizerGap;
  const framesPerBar = state.transcription?.visualizerFramesPerBar;
  const isRecordingLive = recordAudio.isRecording && !isStopping;
  const isFrozen = isStopping || recordAudio.isProcessing;

  const renderStopAction = (): ReactElement => {
    if (recordAudio.isProcessing) {
      return (
        <div
          className={`${recordAudioStyles.controlButton} ${recordAudioStyles.stopButton} ${recordAudioStyles.micButtonWrapper} ${SIZE_CLASS_MAP[state.size]} `}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={window.i18next.t('pmwjs_processing')}
        >
          <CircularProgressLoader color={LOADER_COLOR.WHITE} size={LOADER_SIZE.SMALL} classes="_unmargin" />
        </div>
      );
    }

    return (
      <ClickableDiv
        className={`${recordAudioStyles.controlButton} ${recordAudioStyles.stopButton} ${recordAudioStyles.micButtonWrapper} ${SIZE_CLASS_MAP[state.size]} `}
        aria-label={window.i18next.t('pmwjs_stop_recording')}
        onClick={(e: React.MouseEvent): void => {
          e.stopPropagation();
          setIsStopping(true);
          recordAudio.stopAndSubmit();
        }}
      >
        <span className={recordAudioStyles.tooltip}>{window.i18next.t('pmwjs_stop_recording')}</span>
        <Icon icon="icon-check" size={SIZE_ICON_SIZE_MAP[state.size]} />
      </ClickableDiv>
    );
  };

  return (
    <div className={recordAudioStyles.recordingControlsContainer} style={{height: `${SIZE_HEIGHT_MAP[state.size]}px`}}>
      <div className={recordAudioStyles.visualizerWrapper}>
        <AudioWaveformVisualizer
          analyserNode={recordAudio.analyserNode}
          isRecording={isRecordingLive}
          isFrozen={isFrozen}
          barWidth={barWidth}
          gap={gap}
          framesPerBar={framesPerBar}
        />
      </div>
      <ClickableDiv
        className={`${recordAudioStyles.controlButton} ${recordAudioStyles.cancelButton} ${recordAudio.isProcessing ? recordAudioStyles.processing : ''} ${recordAudioStyles.micButtonWrapper} ${SIZE_CLASS_MAP[state.size]}`}
        aria-label={window.i18next.t('pmwjs_cancel')}
        onClick={(e: React.MouseEvent): void => {
          e.stopPropagation();
          setIsStopping(false);
          recordAudio.cancel();
        }}
      >
        <span className={recordAudioStyles.tooltip}>{window.i18next.t('pmwjs_cancel')}</span>
        <Icon icon="icon-close" size={SIZE_ICON_SIZE_MAP[state.size]} />
      </ClickableDiv>
      {renderStopAction()}
    </div>
  );
}
